/**
 * Widget d'abonnement dynamique pour Shopify
 * Gère les selling plans et les prix dynamiques
 */

class SubscriptionWidget {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.variantId = container.dataset.variantId;
    this.sectionId = container.dataset.sectionId;
    this.basePrice = parseFloat(container.dataset.basePrice) / 100;
    this.comparePrice = parseFloat(container.dataset.comparePrice) / 100;
    
    this.radioButtons = container.querySelectorAll('input[type="radio"]');
    this.selectElement = container.querySelector('.subscription-widget__select');
    this.frequencySelect = container.querySelector('.subscription-widget__frequency-select');
    this.tomSelectInstance = null;
    
    // Observers pour éviter les conflits
    this.priceObserver = null;
    this.buttonObserver = null;
    this.variantChangeUnsubscriber = null;
    
    // Debug
    console.log('SubscriptionWidget initialized:', {
      productId: this.productId,
      variantId: this.variantId,
      sectionId: this.sectionId,
      basePrice: this.basePrice
    });
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.initTomSelect();
    this.subscribeToThemeEvents();
    
    // Initialiser le champ selling_plan
    this.updateSelectedPlan('');
    
    // Mise à jour initiale
    this.updateDisplayPrices();
    this.updateButtonText();
  }
  
  subscribeToThemeEvents() {
    // S'abonner aux événements de changement de variant du thème
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        (event) => {
          if (event.data.sectionId === this.sectionId) {
            this.handleVariantChange(event.data.variant);
          }
        }
      );
    }
    
    // Observer les changements de prix pour contrer les overwrites du thème
    this.observePriceChanges();
  }
  
  observePriceChanges() {
    // Observer les changements dans le widget pour réappliquer nos prix
    this.priceObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          const target = mutation.target;
          // Vérifier si les prix ont été modifiés
          if (target.closest && (
            target.closest('.subscription-widget__one-time-price') ||
            target.closest('.subscription-widget__subscription-price') ||
            target.closest('.subscription-widget__subscription-compare-price')
          )) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        // Réappliquer nos prix après un court délai
        requestAnimationFrame(() => {
          this.updateDisplayPrices();
          this.updateButtonText();
        });
      }
    });
    
    // Observer les changements dans le widget
    this.priceObserver.observe(this.container, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }
  
  initTomSelect() {
    if (this.selectElement && typeof TomSelect !== 'undefined') {
      this.tomSelectInstance = new TomSelect(this.selectElement, {
        placeholder: "Choisissez une fréquence...",
        create: false,
        controlInput: false,
        onChange: (value) => {
          this.handleSelectChange({ target: { value: value } });
        }
      });
    }
  }
  
  bindEvents() {
    // Gestion des boutons radio
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.handleRadioChange(e);
      });
    });
    
    // Écouter les changements de variante via les sélecteurs de variantes
    document.addEventListener('change', (e) => {
      if (e.target.matches('select[name="id"], input[name="id"]')) {
        const variantId = e.target.value;
        this.handleVariantIdChange(variantId);
      }
    });
    
    // Écouter les mises à jour du thème
    document.addEventListener('shopify:section:load', () => {
      requestAnimationFrame(() => {
        this.updateDisplayPrices();
        this.updateButtonText();
      });
    });
  }
  
  handleVariantIdChange(variantId) {
    // Récupérer les informations de la variante depuis le DOM
    const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (variantElement) {
      const price = parseFloat(variantElement.dataset.price) / 100;
      const comparePrice = variantElement.dataset.comparePrice ? parseFloat(variantElement.dataset.comparePrice) / 100 : 0;
      
      this.basePrice = price;
      this.comparePrice = comparePrice;
      this.variantId = variantId;
      
      // Mettre à jour les attributs data
      this.container.dataset.basePrice = price * 100;
      this.container.dataset.comparePrice = comparePrice * 100;
      this.container.dataset.variantId = variantId;
      
      // Mettre à jour l'affichage
      this.updateDisplayPrices();
      this.updateButtonText();
    }
  }
  
  handleVariantChange(variant) {
    // Mettre à jour les prix de base
    this.basePrice = variant.price / 100;
    this.comparePrice = variant.compare_at_price ? variant.compare_at_price / 100 : 0;
    this.variantId = variant.id;
    
    // Mettre à jour les attributs data
    this.container.dataset.basePrice = variant.price;
    this.container.dataset.comparePrice = variant.compare_at_price || 0;
    this.container.dataset.variantId = variant.id;
    
    // Mettre à jour l'affichage
    this.updateDisplayPrices();
    this.updateButtonText();
  }
  
  handleRadioChange(event) {
    const radio = event.target;
    const option = radio.closest('.subscription-widget__option');
    
    // Mettre à jour les classes actives
    this.container.querySelectorAll('.subscription-widget__option').forEach(opt => {
      opt.classList.remove('subscription-widget__option--active');
    });
    
    // Afficher/masquer le select de fréquence
    if (radio.value && this.frequencySelect) {
      option.classList.add('subscription-widget__option--active');
      this.frequencySelect.style.display = 'block';
    } else {
      option.classList.add('subscription-widget__option--active');
      this.frequencySelect.style.display = 'none';
    }
    
    // Mettre à jour le plan sélectionné
    this.updateSelectedPlan(radio.value);
    
    // Mettre à jour seulement le bouton
    this.updateButtonText();
  }
  
  handleSelectChange(event) {
    const sellingPlanId = event.target.value;
    
    // Mettre à jour la valeur du radio button d'abonnement
    const subscriptionRadio = this.container.querySelector('#subscription-recurring-' + this.productId);
    if (subscriptionRadio) {
      subscriptionRadio.value = sellingPlanId;
    }
    
    // Mettre à jour le plan sélectionné
    this.updateSelectedPlan(sellingPlanId);
    
    // Mettre à jour seulement le bouton
    this.updateButtonText();
  }
  
  updateSelectedPlan(sellingPlanId) {
    // Mettre à jour le champ selling_plan dans le formulaire principal du produit
    const productForm = document.querySelector(`#product-form-${this.sectionId}`);
    if (productForm) {
      let sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      
      // Si le champ n'existe pas, le créer
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        productForm.appendChild(sellingPlanInput);
      }
      
      sellingPlanInput.value = sellingPlanId;
      console.log('Updated selling plan in product form:', sellingPlanId);
    }
    
    this.container.dataset.selectedSellingPlan = sellingPlanId;
  }
  
  updateDisplayPrices() {
    const selectedRadio = this.container.querySelector('input[type="radio"]:checked');
    
    // L'achat unique affiche toujours le prix normal de la variante actuelle
    const oneTimePrice = this.container.querySelector('.subscription-widget__one-time-price');
    if (oneTimePrice) {
      oneTimePrice.textContent = this.formatPrice(this.basePrice);
    }
    
    // L'abonnement affiche toujours le prix normal barré + le prix remisé
    const subscriptionPrice = this.container.querySelector('.subscription-widget__subscription-price');
    const subscriptionComparePrice = this.container.querySelector('.subscription-widget__subscription-compare-price');
    
    if (subscriptionPrice && subscriptionComparePrice) {
      // Prix normal barré (toujours le prix de la variante actuelle)
      subscriptionComparePrice.textContent = this.formatPrice(this.basePrice);
      subscriptionComparePrice.style.display = 'inline';
      
      // Prix remisé - se met à jour selon la sélection
      if (selectedRadio && selectedRadio.value) {
        // Si l'abonnement est sélectionné, calculer le prix remisé
        const sellingPlanId = selectedRadio.value;
        const discountPercentage = this.getDiscountPercentage(sellingPlanId);
        
        if (discountPercentage > 0) {
          const discountedPrice = this.basePrice * (1 - discountPercentage / 100);
          subscriptionPrice.textContent = this.formatPrice(discountedPrice);
        } else {
          subscriptionPrice.textContent = this.formatPrice(this.basePrice);
        }
      } else {
        // Si l'achat unique est sélectionné, afficher le prix remisé du plan par défaut
        const defaultSellingPlanId = this.container.querySelector('.subscription-widget__select option:first-child')?.value;
        if (defaultSellingPlanId) {
          const discountPercentage = this.getDiscountPercentage(defaultSellingPlanId);
          if (discountPercentage > 0) {
            const discountedPrice = this.basePrice * (1 - discountPercentage / 100);
            subscriptionPrice.textContent = this.formatPrice(discountedPrice);
          } else {
            subscriptionPrice.textContent = this.formatPrice(this.basePrice);
          }
        }
      }
    }
  }
  
  getDiscountPercentage(sellingPlanId) {
    const select = this.container.querySelector(`option[value="${sellingPlanId}"]`);
    if (select && select.dataset.discount) {
      return parseFloat(select.dataset.discount);
    }
    return 0;
  }
  
  formatPrice(price) {
    if (window.FoxTheme && window.FoxTheme.Currency) {
      return FoxTheme.Currency.formatMoney(price * 100, FoxTheme.settings.moneyFormat);
    }
    
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(price);
  }
  
  updateButtonText() {
    const submitButton = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    const stickyButton = document.querySelector(`#ProductSubmitButton-sticky-${this.sectionId}`);
    
    const selectedRadio = this.container.querySelector('input[type="radio"]:checked');
    
    let finalPrice = this.basePrice;
    if (selectedRadio && selectedRadio.value) {
      const discountPercentage = this.getDiscountPercentage(selectedRadio.value);
      if (discountPercentage > 0) {
        finalPrice = this.basePrice * (1 - discountPercentage / 100);
      }
    }
    
    const formattedPrice = this.formatPrice(finalPrice);
    
    // Mettre à jour le bouton principal
    if (submitButton) {
      const btnText = submitButton.querySelector('.btn__text');
      if (btnText) {
        const priceSpan = btnText.querySelector('.btn__price');
        if (priceSpan) {
          priceSpan.textContent = formattedPrice;
        } else {
          btnText.innerHTML = `${FoxTheme?.variantStrings?.addToCart || 'Ajouter au panier'} | <span class="btn__price">${formattedPrice}</span>`;
        }
      }
    }
    
    // Mettre à jour le bouton sticky
    if (stickyButton) {
      const btnText = stickyButton.querySelector('.btn__text');
      if (btnText) {
        const priceSpan = btnText.querySelector('.btn__price');
        if (priceSpan) {
          priceSpan.textContent = formattedPrice;
        } else {
          btnText.innerHTML = `${FoxTheme?.variantStrings?.addToCart || 'Ajouter au panier'} | <span class="btn__price">${formattedPrice}</span>`;
        }
      }
    }
  }
  
  destroy() {
    // Se désabonner des événements du thème
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    
    // Nettoyer l'instance Tom Select
    if (this.tomSelectInstance) {
      this.tomSelectInstance.destroy();
      this.tomSelectInstance = null;
    }
    
    // Arrêter l'observation des prix
    if (this.priceObserver) {
      this.priceObserver.disconnect();
      this.priceObserver = null;
    }
  }
}

// Initialisation automatique des widgets
document.addEventListener('DOMContentLoaded', () => {
  const widgets = document.querySelectorAll('.subscription-widget');
  widgets.forEach(widget => {
    new SubscriptionWidget(widget);
  });
});

// Support pour les sections dynamiques Shopify
document.addEventListener('shopify:section:load', (event) => {
  const widget = event.target.querySelector('.subscription-widget');
  if (widget) {
    new SubscriptionWidget(widget);
  }
});

// Support pour les sections dynamiques Shopify (nettoyage)
document.addEventListener('shopify:section:unload', (event) => {
  const widget = event.target.querySelector('.subscription-widget');
  if (widget && widget.subscriptionWidget) {
    widget.subscriptionWidget.destroy();
  }
});

// Export pour utilisation globale
window.SubscriptionWidget = SubscriptionWidget;
